import React, { useCallback } from 'react';
import Box, { BoxProps } from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField, { TextFieldProps } from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { LoadingButton, MoneyField, MoneyFieldProps, useNotify } from '@routes-plan/ui';
import PermissionButton from '../../../components/common/PermissionButton';
import { companyCurrency, companyLocale, currencyDecimals } from '../../../utils/format/currency';
import { shouldHideForbiddenActions } from '../../../utils/permission-ui';

type Severity = 'success' | 'info' | 'warning' | 'error';

/**
 * Avisos de las pantallas de Inventario con `useNotify` de la librería (arriba a la derecha; abajo en teléfono).
 * Misma firma que `showAlert` para no cambiar la lógica de cada pantalla.
 */
export const useInventoryAlert = () => {
  const notify = useNotify();
  return useCallback((message: string, severity: Severity = 'success') => notify[severity](message), [notify]);
};

/** Contenedor de la pantalla: bloques uno debajo del otro, sin desbordar a los lados. */
export const PageBody: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0, width: '100%' }}>{children}</Box>
);

/** Fila de filtros: tantas columnas como quepan (mínimo 220 px cada una); en teléfono, una por fila. */
export const FilterGrid: React.FC<BoxProps> = ({ sx, ...rest }) => (
  <Box
    {...rest}
    sx={{
      display: 'grid',
      gap: 2,
      alignItems: 'center',
      gridTemplateColumns: 'repeat(auto-fit, minmax(min(220px, 100%), 1fr))',
      ...(sx as object),
    }}
  />
);

/** Campos de un formulario: una columna en teléfono y `md` columnas (2 por defecto) desde tableta. */
export const FieldGrid: React.FC<BoxProps & { md?: number; lg?: number }> = ({ md = 2, lg, sx, ...rest }) => (
  <Box
    {...rest}
    sx={{
      display: 'grid',
      gap: 2,
      alignItems: 'start',
      gridTemplateColumns: {
        xs: 'minmax(0, 1fr)',
        sm: `repeat(${Math.min(md, 2)}, minmax(0, 1fr))`,
        md: `repeat(${md}, minmax(0, 1fr))`,
        ...(lg ? { lg: `repeat(${lg}, minmax(0, 1fr))` } : {}),
      },
      ...(sx as object),
    }}
  />
);

/** Ocupa toda la fila dentro de `FieldGrid`. */
export const fullRow = { gridColumn: '1 / -1' } as const;

interface SectionProps {
  title?: React.ReactNode;
  description?: React.ReactNode;
  /** Botones o controles a la derecha del título. */
  actions?: React.ReactNode;
  children?: React.ReactNode;
  /** Sin borde ni relleno (dentro de un modal). */
  plain?: boolean;
}

/** Sección de formulario o de consulta: `Paper` con borde, título `h6` y descripción corta. */
export const Section: React.FC<SectionProps> = ({ title, description, actions, children, plain }) => {
  const content = (
    <>
      {(title || actions) && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: 1, mb: description ? 0.5 : 2 }}>
          {title && (
            <Typography variant="h6" component="h2" sx={{ flex: '1 1 auto', minWidth: 0 }}>
              {title}
            </Typography>
          )}
          {actions && <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, ml: 'auto' }}>{actions}</Box>}
        </Box>
      )}
      {description && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {description}
        </Typography>
      )}
      {children}
    </>
  );
  if (plain) return <Box sx={{ minWidth: 0 }}>{content}</Box>;
  return (
    <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, minWidth: 0 }}>
      {content}
    </Paper>
  );
};

/** Acciones al pie de un formulario: secundaria y principal, a la derecha (en teléfono ocupan el ancho). */
export const FormFooter: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box
    sx={{
      display: 'flex',
      flexWrap: 'wrap',
      justifyContent: 'flex-end',
      gap: 1,
      '& > *': { flex: { xs: '1 1 auto', sm: '0 0 auto' } },
    }}
  >
    {children}
  </Box>
);

type MoneyProps = Omit<MoneyFieldProps, 'value' | 'onChange' | 'currency' | 'locale' | 'decimals'> & {
  /** Valor como texto ('' = vacío), igual que lo guardan los formularios. */
  value: string;
  onChange: (value: string) => void;
};

/** Dinero con la moneda y el formato de la empresa (separadores de miles, sin centavos en COP). */
export const CompanyMoneyField: React.FC<MoneyProps> = ({ value, onChange, ...rest }) => (
  <MoneyField
    {...rest}
    value={value === '' || value === null || value === undefined ? null : Number(value)}
    onChange={(v) => onChange(v === null ? '' : String(v))}
    currency={companyCurrency()}
    locale={companyLocale()}
    decimals={currencyDecimals()}
  />
);

/** Cantidades enteras (unidades, días): solo dígitos, con teclado numérico en el teléfono. */
export const IntField: React.FC<Omit<TextFieldProps, 'onChange' | 'value' | 'type'> & { value: string; onChange: (value: string) => void }> = ({
  value,
  onChange,
  inputProps,
  ...rest
}) => (
  <TextField
    {...rest}
    value={value}
    onChange={(e) => onChange(e.target.value.replace(/\D/g, ''))}
    inputProps={{ inputMode: 'numeric', ...inputProps, style: { textAlign: 'right', fontVariantNumeric: 'tabular-nums' } }}
  />
);

interface SaveButtonProps {
  allowed: boolean;
  deniedTooltip: string;
  loading?: boolean;
  disabled?: boolean;
  onClick: () => void;
  color?: 'primary' | 'error';
  startIcon?: React.ReactNode;
  children: React.ReactNode;
}

/** Botón principal de un formulario: `LoadingButton` con permiso; sin permiso, desactivado con su motivo. */
export const SaveButton: React.FC<SaveButtonProps> = ({ allowed, deniedTooltip, loading, disabled, onClick, color = 'primary', startIcon, children }) =>
  allowed ? (
    <LoadingButton variant="contained" color={color} loading={loading} disabled={disabled} onClick={onClick} startIcon={startIcon}>
      {children}
    </LoadingButton>
  ) : (
    <PermissionButton allowed={false} deniedTooltip={deniedTooltip} variant="contained" color={color}>
      {children}
    </PermissionButton>
  );

interface ActionIconProps {
  label: string;
  icon: React.ReactNode;
  onClick?: () => void;
  /** Permiso de la acción; sin permiso se oculta (o se desactiva con su descripción, según la marca). */
  allowed?: boolean;
  deniedTooltip?: string;
  color?: 'default' | 'primary' | 'secondary' | 'error' | 'warning' | 'success' | 'info';
  disabled?: boolean;
}

/** Acción de una fila de tabla: ícono con su descripción al pasar el mouse y `aria-label`. */
export const ActionIcon: React.FC<ActionIconProps> = ({ label, icon, onClick, allowed = true, deniedTooltip, color = 'default', disabled }) => {
  if (!allowed && shouldHideForbiddenActions()) return null;
  const title = allowed ? label : deniedTooltip ?? label;
  return (
    <Tooltip title={title}>
      <span>
        <IconButton size="small" color={color} aria-label={label} onClick={onClick} disabled={disabled || !allowed}>
          {icon}
        </IconButton>
      </span>
    </Tooltip>
  );
};

/** Pestañas de una pantalla, con línea divisoria y desplazamiento en teléfono. */
export const PageTabs: React.FC<{ value: number; onChange: (v: number) => void; labels: string[] }> = ({ value, onChange, labels }) => (
  <Box sx={{ borderBottom: 1, borderColor: 'divider', minWidth: 0 }}>
    <Tabs value={value} onChange={(_, v) => onChange(v)} variant="scrollable" allowScrollButtonsMobile>
      {labels.map((l) => (
        <Tab key={l} label={l} />
      ))}
    </Tabs>
  </Box>
);

